import { toast } from 'react-toastify';
import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { useEquipmentAction } from '@hooks/useEquipmentAction';
import { useShoppingCart } from '@hooks/useShoppingCart/useShoppingCart';
import { useConfigurationStore } from '@store/configurationStore';
import { useQualificationFlowStore } from '@store/qualificationStore/qualificationStore';
import type { Plan } from '@t/productOffering.types';
import type { EquipmentPopupAction } from '@utils/equipmentPopupLogic';
import type { PlanFlowVariables, PlanFlowResult } from './usePlanFlowMutation.types';

export const variablesPlan = (variables: PlanFlowVariables): Plan =>
  variables.kind === 'select' ? variables.plan : variables.newPlan;

export const usePlanFlowMutation = (sessionId: string): UseMutationResult<PlanFlowResult, Error, PlanFlowVariables> => {
  const resolvedAddressId = useQualificationFlowStore((s) => s.resolvedAddressId) ?? '';
  const { selectPlanAsync } = useShoppingCart(sessionId);
  const { resolve: resolveEquipmentAction, autoAddEquipment } = useEquipmentAction(sessionId, resolvedAddressId);
  const openConfigPopup = useConfigurationStore((s) => s.openPopup);

  return useMutation<PlanFlowResult, Error, PlanFlowVariables>({
    mutationFn: async (variables) => {
      const plan = variablesPlan(variables);

      if (plan.isBundle && plan.bundledMemberIds && plan.bundledMemberIds.length > 0) {
        // Bundle: add the bundle (backend expands mandatory members), then handle equipment per member.
        await selectPlanAsync(plan);
        // Resolve every member's action up front so an 'auto-add' member later in the list isn't
        // silently skipped just because an earlier member needs the equipment popup — only one
        // popup can be shown per mutation, so at most the first 'show' member is deferred to it.
        const memberActions = await Promise.all(
          plan.bundledMemberIds.map(async (memberId) => ({
            memberId,
            action: await resolveEquipmentAction(memberId),
          })),
        );
        for (const { memberId, action } of memberActions) {
          if (action === 'auto-add') {
            await autoAddEquipment(memberId);
          }
        }
        const memberNeedingPopup = memberActions.find(({ action }) => action === 'show');
        if (memberNeedingPopup) {
          // Defer to onSuccess: open the equipment popup for the first member that needs it
          return {
            kind: variables.kind,
            plan,
            hasEquipment: true,
            hasAddOns: false,
            bundleEquipmentMemberId: memberNeedingPopup.memberId,
          };
        }
        return {
          kind: variables.kind,
          plan,
          hasEquipment: false,
          hasAddOns: !!plan.bundleAddOnMemberId,
          bundleAddOnPlanId: plan.bundleAddOnMemberId,
        };
      }

      const hasAddOns = plan.addOns.length > 0;
      const action: EquipmentPopupAction = plan.typeName === 'EV' ? 'skip' : await resolveEquipmentAction(plan.planId);
      const hasEquipment = action === 'show';

      // EV offering with bundle relationships — defer cart addition until user picks a related offering
      if (plan.typeName === 'EV' && plan.bundleRelationships.length > 0) {
        return { kind: variables.kind, plan, hasEquipment: false, hasAddOns: false, hasBundleRelationship: true };
      }

      if (hasEquipment) {
        return { kind: variables.kind, plan, hasEquipment, hasAddOns };
      }

      await selectPlanAsync(plan);
      if (action === 'auto-add') {
        await autoAddEquipment(plan.planId);
      }
      return { kind: variables.kind, plan, hasEquipment, hasAddOns };
    },
    onSuccess: ({ plan, hasEquipment, hasAddOns, bundleEquipmentMemberId, bundleAddOnPlanId, hasBundleRelationship }) => {
      // Dialog is opened by useSelectPlanFlow reading planFlow.data.hasBundleRelationship
      if (hasBundleRelationship) return;
      const baseConfig = {
        planId: plan.planId,
        planName: plan.name,
        planCategory: plan.typeName,
        parentOfferingCode: plan.planId,
        homeIdOfQualifiedOffers: resolvedAddressId,
        sessionId,
      };

      if (hasEquipment) {
        const equipmentConfig = bundleEquipmentMemberId
          ? {
              ...baseConfig,
              planId: bundleEquipmentMemberId,
              parentOfferingCode: bundleEquipmentMemberId,
              pendingPlanAddition: false,
              // transitionToAddOns uses bundleAddOnPlanId (bundle's planId) for cache lookup,
              // bundleAddOnParentCode (Internet UUID) for the cart addChildItem API call,
              // and bundleAddOnMemberCategory to route to the correct add-ons step.
              bundleAddOnPlanId: plan.bundleAddOnMemberId ? plan.planId : undefined,
              bundleAddOnParentCode: plan.bundleAddOnMemberId,
              bundleAddOnMemberCategory: plan.bundleAddOnMemberCategory,
            }
          : { ...baseConfig, pendingPlanAddition: true };
        openConfigPopup({ ...equipmentConfig, hasEquipment: true, hasAddOns: hasAddOns || !!plan.bundleAddOnMemberId });
        return;
      }

      if (!hasAddOns) {
        toast.success('The plan and equipment were successfully added to the cart');
        return;
      }

      toast.success('The plan was successfully added to the cart');

      // useAddOnsForPlan looks up by planId in the React Query cache — pass the bundle's planId.
      // parentOfferingCode must be the Internet member's UUID so addChildItem targets the right cart item.
      const addOnTargetCategory = (bundleAddOnPlanId ? plan.bundleAddOnMemberCategory : undefined) ?? plan.typeName;
      const popupConfig = {
        ...baseConfig,
        planId: plan.planId,
        planCategory: addOnTargetCategory,
        parentOfferingCode: bundleAddOnPlanId ?? plan.planId,
        hasEquipment: false,
        hasAddOns,
      };
      openConfigPopup(popupConfig);
    },
    onError: (_err, variables) => {
      const message =
        variables.kind === 'select'
          ? 'Something went wrong while adding the plan to your cart. Please try again.'
          : 'Something went wrong while switching plans. Please try again.';
      toast.error(message);
    },
  });
};
